import {
  Button,
  Caption1,
  CounterBadge,
  Divider,
  makeStyles,
  Text,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import type { ReactNode } from 'react';

const useStyles = makeStyles({
  // Pílula sobre o conteúdo: sombra forte para se separar da tabela embaixo.
  bar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS} ${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    borderRadius: tokens.borderRadiusCircular,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow28,
    // Sobe do pé da tela ao aparecer.
    animationName: {
      from: { opacity: 0, transform: 'translateY(16px) scale(0.98)' },
      to: { opacity: 1, transform: 'translateY(0) scale(1)' },
    },
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveDecelerateMid,
    '@media (prefers-reduced-motion: reduce)': { animationDuration: '1ms' },
    // No celular, as ações quebram para baixo: cantos menos redondos.
    '@media (max-width: 639px)': { borderRadius: tokens.borderRadiusXLarge },
  },
  summary: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  text: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  detail: { color: tokens.colorNeutralForeground3 },
  divider: {
    flexGrow: 0,
    height: '28px',
    '@media (max-width: 639px)': { display: 'none' },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
});

export interface FloatingActionBarProps {
  /** Quantos itens: com mais de um, aparece em destaque antes do rótulo. */
  readonly count: number;
  /** O que está selecionado, em uma linha ("caixas selecionadas"). */
  readonly label: ReactNode;
  /** Uma observação abaixo do rótulo, quando houver. */
  readonly detail?: ReactNode;
  /** Desfaz a seleção. */
  onDismiss(): void;
  readonly dismissLabel: string;
  /** Os botões das ações (de preferência `shape="circular"`). */
  readonly children: ReactNode;
}

/**
 * Ações sobre os itens selecionados, flutuando no pé da página
 * (`PageShell.floating`). Anunciada aos leitores de tela quando muda.
 */
export function FloatingActionBar({
  count,
  label,
  detail,
  onDismiss,
  dismissLabel,
  children,
}: FloatingActionBarProps) {
  const styles = useStyles();
  return (
    <div className={styles.bar} role="toolbar" aria-label="Ações da seleção">
      <div className={styles.summary}>
        {count > 1 && (
          <CounterBadge
            count={count}
            overflowCount={999}
            size="large"
            appearance="filled"
            color="brand"
            aria-hidden
          />
        )}
        <div className={styles.text} aria-live="polite">
          <Text weight="semibold">{label}</Text>
          {detail !== undefined && (
            <Caption1 className={styles.detail}>{detail}</Caption1>
          )}
        </div>
      </div>
      <Divider vertical className={styles.divider} />
      <div className={styles.actions}>
        {children}
        <Button
          appearance="subtle"
          shape="circular"
          icon={<DismissRegular />}
          aria-label={dismissLabel}
          title={dismissLabel}
          onClick={onDismiss}
        />
      </div>
    </div>
  );
}
